import { Rocket } from "lucide-react"
import { observer } from "mobx-react"
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Z_INDEX } from "../../constants"
import { usePortalContainer } from "../../hooks/usePortalContainer"
import { queueModeManager } from "./queueModeManager"

const LAUNCH_DURATION_MS = 2_800

export const QueueModeLaunch = observer(function QueueModeLaunch() {
    const startedAtMs = queueModeManager.launchStartedAtMs
    if (!queueModeManager.isActive || startedAtMs === null) return null

    return (
        <LaunchSequence
            key={queueModeManager.runId}
            startedAtMs={startedAtMs}
            readyCount={queueModeManager.eligibleTaskCount}
            taskLimit={queueModeManager.maxConcurrent}
        />
    )
})

function LaunchSequence({ startedAtMs, readyCount, taskLimit }: { startedAtMs: number; readyCount: number; taskLimit: number }) {
    const portalContainer = usePortalContainer()
    const anchorRef = useRef<HTMLDivElement>(null)
    const [bounds, setBounds] = useState<DOMRect | null>(null)
    // A route change can remount the shell. Continue the same launch instead of replaying it.
    const [elapsedAtMount] = useState(() => Math.max(0, Date.now() - startedAtMs))
    const [finished, setFinished] = useState(elapsedAtMount >= LAUNCH_DURATION_MS)

    useLayoutEffect(() => {
        const anchor = anchorRef.current
        if (finished || !anchor) return
        // Only the brief ceremony measures layout. The portal dims the whole app while
        // the scene follows the conversation as either sidebar or dock changes width.
        let frame: number | null = null
        const measure = () => {
            frame = null
            const next = anchor.getBoundingClientRect()
            setBounds((previous) =>
                previous && previous.left === next.left && previous.top === next.top && previous.width === next.width && previous.height === next.height
                    ? previous
                    : next
            )
        }
        // ResizeObserver and window resize can report the same change together.
        // Read geometry at most once per frame; unchanged bounds don't rerender flights.
        const scheduleMeasure = () => {
            if (frame === null) frame = window.requestAnimationFrame(measure)
        }
        measure()
        const observer = new ResizeObserver(scheduleMeasure)
        observer.observe(anchor)
        window.addEventListener("resize", scheduleMeasure)
        return () => {
            observer.disconnect()
            window.removeEventListener("resize", scheduleMeasure)
            if (frame !== null) window.cancelAnimationFrame(frame)
        }
    }, [finished])

    useEffect(() => {
        const remainingMs = startedAtMs + LAUNCH_DURATION_MS - Date.now()
        if (remainingMs <= 0) {
            setFinished(true)
            return
        }
        const timeout = window.setTimeout(() => setFinished(true), remainingMs)
        return () => window.clearTimeout(timeout)
    }, [startedAtMs])

    if (finished) return null

    const timing: CSSProperties & { "--launch-duration": string; "--launch-offset": string } = {
        zIndex: Z_INDEX.QUEUE_HUD,
        "--launch-duration": `${LAUNCH_DURATION_MS}ms`,
        "--launch-offset": `-${elapsedAtMount}ms`,
    }
    const scene = bounds && (
        <div className="queue-launch" style={timing} role="status" data-queue-launch="true">
            <span className="sr-only">Queue mode engaged.</span>
            <div className="queue-launch-backdrop" aria-hidden="true" />
            <div className="queue-launch-pane" style={{ left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height }} aria-hidden="true">
                <div className="queue-launch-grid" />
                <div className="queue-launch-fleet">
                    {[0, 1, 2, 3].map((flight) => (
                        <span className="queue-launch-ship" key={flight}>
                            <Rocket size={48} strokeWidth={1.5} />
                            <span className="queue-launch-exhaust" />
                        </span>
                    ))}
                </div>
                <div className="queue-launch-scene">
                    <svg className="queue-launch-streaks" viewBox="0 0 800 400" fill="none">
                        <path d="M285 160 65 30 M300 210 12 245 M305 245 180 385 M330 145 270 12 M515 160 735 30 M500 210 788 245 M495 245 620 385 M470 145 530 12" />
                        <path d="M245 125 210 100 M208 218 162 225 M293 288 275 320 M555 125 590 100 M592 218 638 225 M507 288 525 320" />
                    </svg>
                    <div className="queue-launch-card">
                        <div className="queue-launch-flight">
                            <span className="queue-launch-orbit" />
                            <span className="queue-launch-rocket">
                                <Rocket size={32} strokeWidth={1.5} />
                            </span>
                        </div>
                        <div className="queue-launch-copy">
                            <span className="queue-launch-eyebrow">
                                Q MODE <span>//</span> ENGAGED
                            </span>
                            <strong className="queue-launch-title">LET’S GO.</strong>
                            <span className="queue-launch-detail">
                                <span>
                                    <b>{readyCount}</b> {readyCount === 1 ? "task" : "tasks"} ready
                                </span>
                                <span>
                                    <b>{taskLimit}</b> task limit
                                </span>
                            </span>
                        </div>
                        <div className="queue-launch-power">
                            <span />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    return (
        <>
            <div ref={anchorRef} className="queue-launch-anchor" aria-hidden="true" />
            {portalContainer && scene ? createPortal(scene, portalContainer) : null}
        </>
    )
}
